import React, { useEffect, useState } from 'react';
import { getAllFeedback, archiveFeedback } from '../../services/feedbackService';
import AdminLayout from '../../components/admin/AdminLayout';
import { useConfirmation } from '../../contexts/ConfirmationContext';
import { useNotification } from '../../components/framework/NotificationContext';

const FeedbackListPage = () => {
    const confirm = useConfirmation();
    const [feedbacks, setFeedbacks] = useState([]);
    const [error, setError] = useState('');
    const { success, error: notifyError } = useNotification();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, active: 0, archived: 0 });

    useEffect(() => {
        fetchFeedback();
    }, []);

    async function fetchFeedback() {
        setLoading(true);
        try {
            const data = await getAllFeedback();
            const list = data || [];
            setFeedbacks(list);

            // Calculate stats
            const calculations = list.reduce((acc, item) => {
                acc.total++;
                if (item.archived) acc.archived++;
                else acc.active++;
                return acc;
            }, { total: 0, active: 0, archived: 0 });
            setStats(calculations);

        } catch (err) {
            setError(err.message || 'Failed to fetch feedback');
        } finally {
            setLoading(false);
        }
    }

    const handleArchive = async (id) => {
        const isConfirmed = await confirm({
            title: "Archive Feedback",
            message: "Move this message to archive?",
            confirmLabel: "Archive",
            type: "warning"
        });

        if (!isConfirmed) return;

        try {
            await archiveFeedback(id);
            // Refresh list
            fetchFeedback();
            success("Feedback archived");
        } catch (err) {
            notifyError(err.message || "Failed to archive feedback");
        }
    };

    const activeFeedbacks = feedbacks.filter(f => !f.archived);
    const archivedFeedbacks = feedbacks.filter(f => f.archived);

    const FeedbackTable = ({ data, title, isActive }) => (
        <div className="admin-card" style={{ marginBottom: '2.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h4 style={{ margin: 0, fontSize: '1.25rem', color: '#002244', fontWeight: 600 }}>
                    {title} <span style={{ fontSize: '0.9rem', fontWeight: 400, color: '#6c757d', marginLeft: '0.5rem' }}>({data.length})</span>
                </h4>
            </div>

            <div className="table-responsive">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Contact Information</th>
                            <th>Topic</th>
                            <th>Message Content</th>
                            {isActive && <th style={{ textAlign: 'right' }}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr>
                                <td colSpan={isActive ? "5" : "4"} style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
                                    <i className={`bi ${isActive ? 'bi-chat-left-text' : 'bi-archive'}`} style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}></i>
                                    {isActive ? 'No active feedback messages.' : 'No archived messages yet.'}
                                </td>
                            </tr>
                        ) : (
                            data.map((item) => (
                                <tr key={item.feedbackId}>
                                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem', color: '#6c757d' }}>
                                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        }) : '-'}
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontWeight: 600, color: '#002244' }}>{item.name || 'Anonymous'}</span>
                                            <span style={{ fontSize: '0.85rem', color: '#0056b3' }}>{item.email || '-'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                            <span className="status-badge" style={{ backgroundColor: '#e2e3e5', color: '#383d41', alignSelf: 'flex-start' }}>
                                                {item.category}
                                            </span>
                                            <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{item.subject}</span>
                                        </div>
                                    </td>
                                    <td style={{ maxWidth: '400px', fontSize: '0.95rem' }}>
                                        <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                                            {item.message}
                                        </div>
                                        {item.imageBase64 && (
                                            <div style={{ marginTop: '0.5rem' }}>
                                                <button
                                                    className="btn btn-sm btn-link p-0"
                                                    onClick={() => window.open(`data:image/png;base64,${item.imageBase64}`)}
                                                    style={{ fontSize: '0.8rem' }}
                                                >
                                                    <i className="bi bi-image"></i> View Attachment
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                    {isActive && (
                                        <td style={{ textAlign: 'right' }}>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => handleArchive(item.feedbackId)}
                                                title="Move to Archive"
                                                style={{ borderRadius: '6px' }}
                                            >
                                                <i className="bi bi-archive"></i> Archive
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <AdminLayout
            title="User Feedback"
            subtitle="Manage and archive reports submitted by researchers and contributors."
        >
            {/* Summary Statistics Row */}
            <div className="dashboard-summary-row" style={{ marginBottom: '2rem' }}>
                <div className="summary-card">
                    <div className="summary-icon" style={{ backgroundColor: 'rgba(0, 34, 68, 0.1)', color: '#002244' }}>
                        <i className="bi bi-chat-dots"></i>
                    </div>
                    <div className="summary-info">
                        <span className="summary-label">Total Messages</span>
                        <span className="summary-value">{loading ? '-' : stats.total}</span>
                    </div>
                </div>
                <div className="summary-card">
                    <div className="summary-icon" style={{ backgroundColor: 'rgba(25, 135, 84, 0.1)', color: '#198754' }}>
                        <i className="bi bi-envelope-exclamation"></i>
                    </div>
                    <div className="summary-info">
                        <span className="summary-label">Active (Unread)</span>
                        <span className="summary-value">{loading ? '-' : stats.active}</span>
                    </div>
                </div>
                <div className="summary-card">
                    <div className="summary-icon" style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', color: '#6c757d' }}>
                        <i className="bi bi-archive"></i>
                    </div>
                    <div className="summary-info">
                        <span className="summary-label">Archived</span>
                        <span className="summary-value">{loading ? '-' : stats.archived}</span>
                    </div>
                </div>
            </div>

            {error && <div className="alert alert-danger" style={{ borderRadius: '8px', marginBottom: '2rem' }}>{error}</div>}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '5rem' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading feedback messages...</p>
                </div>
            ) : (
                <>
                    <FeedbackTable data={activeFeedbacks} title="Active Feedback" isActive={true} />
                    <FeedbackTable data={archivedFeedbacks} title="Archived Messages" isActive={false} />
                </>
            )}
        </AdminLayout>
    );
};

export default FeedbackListPage;
